import { useEffect, useState } from 'react';
import { ThirdPartyIntegrationVendor } from '@bumble/app/__generated__/Api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

interface ThirdPartyIntegrationFormProps {
  vendors: ThirdPartyIntegrationVendor[];
  fieldsDisabled?: boolean;
  submitting?: boolean;
  onSubmit?: (vendor: string, config: { key: string; value: string }[]) => void;
}

export const ThirdPartyIntegrationForm = ({
  vendors,
  fieldsDisabled = false,
  submitting = false,
  onSubmit,
}: ThirdPartyIntegrationFormProps) => {
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [configValues, setConfigValues] = useState<Record<string, string>>({});

  const currentVendor = vendors.find((v) => v.vendor === selectedVendor);

  useEffect(() => {
    if (!selectedVendor && vendors.length > 0) {
      setSelectedVendor(vendors[0].vendor);
    }
  }, [vendors]);

  useEffect(() => {
    if (!currentVendor) return;
    const values: Record<string, string> = {};
    currentVendor.config.forEach((field) => {
      values[field.key] = field.value || '';
    });
    setConfigValues(values);
  }, [currentVendor?.vendor]);

  const handleVendorChange = (e: SelectChangeEvent) => {
    setSelectedVendor(e.target.value);
  };

  const handleFieldChange = (key: string, value: string) => {
    setConfigValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!currentVendor) return;
    onSubmit?.(
      selectedVendor,
      currentVendor.config.map((field) => ({
        key: field.key,
        value: configValues[field.key] || '',
      })),
    );
  };

  return (
    <Box pt={2}>
      <FormControl fullWidth sx={{ mb: 6 }}>
        <InputLabel id="external-api-vendor-label">Vendor</InputLabel>
        <Select
          labelId="external-api-vendor-label"
          label="Vendor"
          value={selectedVendor}
          onChange={handleVendorChange}
        >
          {vendors.map(({ vendor }) => (
            <MenuItem value={vendor} key={vendor}>{vendor}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {currentVendor?.config.map((field) => (
        <Box key={field.key} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {field.label || field.key}
            </Typography>
            {field.description && (
              <Tooltip arrow title={field.description}>
                <IconButton color="primary" size="small" sx={{ ml: 0.5 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            disabled={fieldsDisabled}
            type={field.key.toLowerCase().includes('secret') ? 'password' : 'text'}
            value={configValues[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        </Box>
      ))}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={!currentVendor || submitting}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
